import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { getServiceOrders, checkCashRegister } from '../lib/api';
import { toast } from 'sonner';
import { Plus, Search } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const STATUS_OPTIONS = [
  { value: '',          label: 'Todos' },
  { value: 'RECIBIDO',   label: 'Recibido' },
  { value: 'EN_PROCESO', label: 'En proceso' },
  { value: 'LISTO',      label: 'Listo' },
  { value: 'ENTREGADO',  label: 'Entregado' },
  { value: 'CANCELADO',  label: 'Cancelado' },
];

const STATUS_COLORS = {
  RECIBIDO:   'bg-blue-100 text-blue-800',
  EN_PROCESO: 'bg-yellow-100 text-yellow-800',
  LISTO:      'bg-green-100 text-green-800',
  ENTREGADO:  'bg-slate-100 text-slate-700',
  CANCELADO:  'bg-red-100 text-red-700',
};

const money = (n) =>
  Number(n || 0).toLocaleString('es-DO', { style: 'currency', currency: 'DOP' });

const ServiceOrders = () => {
  const { user } = useAuth();
  const navigate  = useNavigate();

  const [loading, setLoading]       = useState(false);
  const [cajaOpen, setCajaOpen]     = useState(true);
  const [orders, setOrders]         = useState([]);
  const [page, setPage]             = useState(1);
  const [perPage, setPerPage]       = useState(25);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);

  const [statusFilter, setStatusFilter] = useState('');
  const [searchQ, setSearchQ]           = useState('');

  useEffect(() => {
    checkCashRegister()
      .then(res => setCajaOpen(!res.required || res.open))
      .catch(() => setCajaOpen(true));
    load(1, perPage, '', '');
    // eslint-disable-next-line
  }, []);

  const load = async (targetPage = page, targetPerPage = perPage, status = statusFilter, q = searchQ) => {
    try {
      setLoading(true);
      const filters = {};
      if (status) filters.status = status;
      if (q)      filters.q      = q;
      const data = await getServiceOrders(filters, targetPage, targetPerPage);
      setOrders(data.items || []);
      setTotalPages(data.total_pages || 1);
      setTotalRecords(data.total || 0);
      setPage(targetPage);
      setPerPage(targetPerPage);
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Error cargando órdenes');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => load(1, perPage, statusFilter, searchQ);

  const clearFilters = () => {
    setStatusFilter('');
    setSearchQ('');
    load(1, perPage, '', '');
  };

  const handleStatusChip = (val) => {
    setStatusFilter(val);
    load(1, perPage, val, searchQ);
  };

  return (
    <div className="p-4 space-y-6" data-testid="service-orders-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>
            Órdenes de Servicio
          </h1>
          <p className="text-slate-600">
            Artículos dejados por el cliente para bordar, personalizar, etc.
          </p>
        </div>
        <Button
          className="gap-2 bg-cyan-500 hover:bg-cyan-600 text-white"
          onClick={() => {
            if (!cajaOpen) { toast.warning('Abre un turno de caja para crear órdenes'); return; }
            navigate('/service-orders/new');
          }}
        >
          <Plus className="w-4 h-4" />
          Nueva orden
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Filtros</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Status chips */}
          <div className="flex flex-wrap gap-2">
            {STATUS_OPTIONS.map((s) => (
              <button
                key={s.value}
                onClick={() => handleStatusChip(s.value)}
                className={`px-3 py-1 rounded-full text-sm font-medium border transition-colors ${
                  statusFilter === s.value
                    ? 'bg-cyan-500 text-white border-cyan-500'
                    : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>

          {/* Search + actions */}
          <div className="flex gap-2">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Cliente o código SRV-..."
                value={searchQ}
                onChange={(e) => setSearchQ(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && applyFilters()}
                className="pl-9"
              />
            </div>
            <Button onClick={applyFilters} disabled={loading} className="gap-2">
              <Search className="w-4 h-4" />
              Buscar
            </Button>
            <Button variant="outline" onClick={clearFilters} disabled={loading}>
              Limpiar
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Listado</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-slate-600">
                  <th className="py-2 pr-4">Código</th>
                  <th className="py-2 pr-4">Fecha</th>
                  <th className="py-2 pr-4">Cliente</th>
                  <th className="py-2 pr-4">Trabajo</th>
                  <th className="py-2 pr-4">Entrega</th>
                  <th className="py-2 pr-4">Estatus</th>
                  <th className="py-2 pr-4">Balance</th>
                  <th className="py-2 pr-4"></th>
                </tr>
              </thead>
              <tbody>
                {orders.map((o) => (
                  <tr key={o.id} className="border-b hover:bg-slate-50 transition-colors">
                    <td className="py-2 pr-4 font-mono text-xs">{o.code}</td>
                    <td className="py-2 pr-4 text-slate-500">
                      {o.received_at
                        ? new Date(o.received_at).toLocaleString('es-DO', { dateStyle: 'short' })
                        : '-'}
                    </td>
                    <td className="py-2 pr-4 font-medium capitalize">{o.customer_name}</td>
                    <td className="py-2 pr-4">
                      {(() => {
                        const hasSubs = (o.sub_orders || []).length > 0;
                        if (hasSubs) {
                          const count = o.sub_orders.length;
                          const svcs = [...new Set(o.sub_orders.flatMap(s => (s.services || []).map(sv => sv.name)))];
                          return (
                            <>
                              <span className="text-slate-700 font-medium">{count} {count === 1 ? 'prenda' : 'prendas'}</span>
                              {svcs.length > 0 && <p className="text-xs text-slate-400">{svcs.join(', ')}</p>}
                            </>
                          );
                        }
                        const article = o.item_product_name || 'Artículo externo';
                        const variant = o.item_variant_size
                          ? ` / ${o.item_variant_size}${o.item_variant_color ? ` · ${o.item_variant_color}` : ''}`
                          : '';
                        const svcs = (o.services || []).map(s => s.name).join(', ');
                        return (
                          <>
                            <span className="text-slate-700 font-medium">{article}{variant}</span>
                            {svcs && <p className="text-xs text-slate-400">{svcs}</p>}
                          </>
                        );
                      })()}
                    </td>
                    <td className="py-2 pr-4 text-slate-500">
                      {o.due_date
                        ? new Date(o.due_date).toLocaleDateString('es-DO')
                        : '-'}
                    </td>
                    <td className="py-2 pr-4">
                      <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-semibold ${STATUS_COLORS[o.status] || 'bg-slate-100 text-slate-600'}`}>
                        {STATUS_OPTIONS.find(s => s.value === o.status)?.label || o.status}
                      </span>
                    </td>
                    <td className="py-2 pr-4 font-mono text-sm">
                      {money(o.pricing?.balance || 0)}
                    </td>
                    <td className="py-2 pr-4">
                      <Link to={`/service-orders/${o.id}`}>
                        <Button variant="outline" size="sm">Detalles</Button>
                      </Link>
                    </td>
                  </tr>
                ))}
                {orders.length === 0 && (
                  <tr>
                    <td colSpan={9} className="py-8 text-center text-slate-500">
                      {loading ? 'Cargando...' : 'No hay órdenes con esos filtros.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 px-4 py-3 border-t border-slate-100">
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <span>Mostrar</span>
              <select
                value={perPage}
                onChange={(e) => { const v = Number(e.target.value); load(1, v, statusFilter, searchQ); }}
                className="border border-slate-200 rounded-lg px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-400"
              >
                {[10, 25, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
              </select>
              <span>filas &nbsp;·&nbsp; {totalRecords} órdenes en total</span>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={() => load(1, perPage, statusFilter, searchQ)} disabled={page === 1}
                className="px-2 py-1.5 rounded-lg text-sm border border-slate-200 disabled:opacity-40 hover:bg-slate-50" title="Primera">«</button>
              <button onClick={() => load(page - 1, perPage, statusFilter, searchQ)} disabled={page === 1}
                className="px-3 py-1.5 rounded-lg text-sm border border-slate-200 disabled:opacity-40 hover:bg-slate-50">‹ Anterior</button>
              <span className="px-4 py-1.5 text-sm text-slate-600">
                Página <strong>{page}</strong> de <strong>{totalPages}</strong>
              </span>
              <button onClick={() => load(page + 1, perPage, statusFilter, searchQ)} disabled={page >= totalPages}
                className="px-3 py-1.5 rounded-lg text-sm border border-slate-200 disabled:opacity-40 hover:bg-slate-50">Siguiente ›</button>
              <button onClick={() => load(totalPages, perPage, statusFilter, searchQ)} disabled={page >= totalPages}
                className="px-2 py-1.5 rounded-lg text-sm border border-slate-200 disabled:opacity-40 hover:bg-slate-50" title="Última">»</button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ServiceOrders;
